import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Config } from "../../utils/config";
import MenuItem from "./MenuItem";
import ErrorBoundary from "../../ErrorBoundary";

export default function BuyOneGetOnePopupMenu({
  isOpen,
  setOpen,
  menuList,
  selectedPromo,
  restaurantDetail,
  orderingMethod,
  sizesOrderingMethod,
  selectedType,
  isDineIn,
  cartId,
  id,
  authToken,
  setCartTotal,
  setSelectedType,
}) {
  const { t } = useTranslation();

  if (!selectedPromo) return null;

  const [isItemOpen, setItemOpen] = React.useState(false);
  const [updateData, setUpdateData] = React.useState(null);
  const [selectionStep, setSelectionStep] = React.useState(1);
  const [firstItemSelected, setFirstItemSelected] = React.useState(null);

  const eligibleItems1 = selectedPromo.details.eligible_items_1 || [];
  const eligibleItems2 = selectedPromo.details.eligible_items_2 || [];

  const processEligibleItems = (itemsList) => {
    const eligibleItemIds: string[] = [];
    const eligibleSizesMap = new Map();

    itemsList.forEach((category) => {
      category.items.forEach((item) => {
        let isItemEligible = item.checked;
        let eligibleSizes: string[] = [];

        if (item.sizes && item.sizes.length > 0) {
          item.sizes.forEach((size) => {
            if (size.checked) {
              eligibleSizes.push(size.id);
            }
          });
        }

        const isFinallyEligible = isItemEligible || eligibleSizes.length > 0;

        if (isFinallyEligible) {
          eligibleItemIds.push(item.id);

          if (eligibleSizes.length > 0) {
            eligibleSizesMap.set(item.id, eligibleSizes);
          }
        }
      });
    });

    return { eligibleItemIds, eligibleSizesMap };
  };

  const {
    eligibleItemIds: eligibleItemIds1,
    eligibleSizesMap: eligibleSizesMap1,
  } = processEligibleItems(eligibleItems1);

  const {
    eligibleItemIds: eligibleItemIds2,
    eligibleSizesMap: eligibleSizesMap2,
  } = processEligibleItems(eligibleItems2);

  const currentEligibleItemIds =
    selectionStep === 1 ? eligibleItemIds1 : eligibleItemIds2;
  const currentEligibleSizesMap =
    selectionStep === 1 ? eligibleSizesMap1 : eligibleSizesMap2;

  const getFilteredContainers = (eligibleItemIds, eligibleSizesMap) => {
    return menuList.containerOrder
      .filter((containerId) => {
        const container = menuList.containers[containerId.id];

        if (!container || container.visibility === 0) {
          return false;
        }

        const hasEligibleItems = container.items.some((menu) => {
          if (
            menu.visibility === 0 ||
            menu[orderingMethod] === "0.00" ||
            menu.availability !== "in_stock"
          ) {
            return false;
          }

          return eligibleItemIds.includes(menu.id);
        });

        return hasEligibleItems;
      })
      .map((containerId) => menuList.containers[containerId.id]);
  };

  const filteredContainers = getFilteredContainers(
    currentEligibleItemIds,
    currentEligibleSizesMap
  );

  const handleItemClick = (menu) => {
    if (menu.availability === "in_stock" && menu.is_available) {
      if (selectionStep === 1) {
        const menuWithPromoInfo = {
          ...menu,
          bogoPromo: true,
          bogoStep: 1,
          promoId: selectedPromo.id,
        };

        if (currentEligibleSizesMap.has(menu.id)) {
          menuWithPromoInfo.eligibleSizes = currentEligibleSizesMap.get(
            menu.id
          );
        }

        setUpdateData(menuWithPromoInfo);
        setItemOpen(true);
      } else if (selectionStep === 2) {
        const menuWithPromoInfo = {
          ...menu,
          bogoPromo: true,
          bogoStep: 2,
          promoDiscount: selectedPromo.details.discount || 100,
          promoId: selectedPromo.id,
          firstItemInfo: firstItemSelected,
        };

        if (currentEligibleSizesMap.has(menu.id)) {
          menuWithPromoInfo.eligibleSizes = currentEligibleSizesMap.get(
            menu.id
          );
        }

        setUpdateData(menuWithPromoInfo);
        setItemOpen(true);
      }
    }
  };

  const handleFirstItemAdded = (itemData) => {
    setFirstItemSelected(itemData);
    setSelectionStep(2);
  };

  const handleSecondItemAdded = () => {
    setOpen(false);
    setSelectionStep(1);
    setFirstItemSelected(null);
  };

  const handleMenuItemClose = (addedToCart, itemData) => {
    setItemOpen(false);

    if (addedToCart) {
      if (selectionStep === 1) {
        handleFirstItemAdded(itemData);
      } else {
        handleSecondItemAdded();
      }
    }
  };

  const getDialogTitle = () => {
    if (selectionStep === 1) {
      return t("coupon.bogo.select_first_item");
    } else {
      return t("coupon.bogo.select_second_item");
    }
  };

  return (
    <>
      <ErrorBoundary>
        <Dialog
          open={isOpen}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setSelectionStep(1);
              setFirstItemSelected(null);
            }
            setOpen(isOpen);
          }}
        >
          <DialogContent className="p-0 rounded-lg sm:rounded-[20px] overflow-y-auto max-h-[75vh]">
            <DialogHeader>
              <div className="px-4 pt-5 pb-4 bg-green">
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedPromo.name}</span>
                </DialogTitle>
                <DialogDescription>
                  {selectedPromo.description}
                </DialogDescription>
              </div>
            </DialogHeader>

            {selectionStep === 1 ? (
              <div className="px-4">
                <div className="mt-2 font-semibold text-xl">
                  {getDialogTitle()}
                </div>
              </div>
            ) : (
              <div className="p-4">
                {selectedPromo.description}
                <div className="mt-2 font-semibold text-xl">
                  {getDialogTitle()}
                </div>
                {firstItemSelected && (
                  <div className="mt-1 text-sm">
                    {t("coupon.bogo.first_item_selected")}: {firstItemSelected}
                  </div>
                )}
              </div>
            )}

            <div className="p-4 max-h-full overflow-y-auto custom-scrollbar">
              {filteredContainers.length === 0 ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex justify-center">
                      <Label>{t("no_eligible_items_found")}</Label>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {filteredContainers.map((container, index) => {
                    const filteredItems = container.items.filter((menu) => {
                      if (
                        menu.visibility === 0 ||
                        menu.availability !== "in_stock"
                      ) {
                        return false;
                      }

                      if (menu[orderingMethod] === null) {
                        if (
                          menu?.sizes &&
                          menu.sizes[0] &&
                          menu?.sizes[0][sizesOrderingMethod] === "0.00"
                        ) {
                          return false;
                        }
                      } else if (menu[orderingMethod] === "0.00") {
                        return false;
                      }
                      return currentEligibleItemIds.includes(menu.id);
                    });

                    if (filteredItems.length === 0) return null;

                    return (
                      <div className="space-y-3" key={index}>
                        <div className="sticky top-0 bg-white z-10 py-2 flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-primary">
                            {container?.title}
                          </h3>
                          {selectionStep === 2 && (
                            <Badge className="bg-primary text-white text-xs">
                              {selectedPromo.details.discount || 100}%{" "}
                              {t("off")}
                            </Badge>
                          )}
                        </div>

                        <div className="grid gap-2">
                          {filteredItems.map((menu, itemIndex) => {
                            const isMenuAvailable = menu.is_available;
                            const originalPrice = isDineIn
                              ? menu?.dineInPrice === null
                                ? parseFloat(
                                    menu?.sizes[0]?.dine_in_price
                                  ).toFixed(2)
                                : parseFloat(menu?.dineInPrice).toFixed(2)
                              : menu[orderingMethod] === null
                              ? parseFloat(
                                  menu?.sizes[0]?.[sizesOrderingMethod]
                                ).toFixed(2)
                              : parseFloat(menu[orderingMethod]).toFixed(2);

                            const hasSizeRestrictions =
                              currentEligibleSizesMap.has(menu.id);

                            return (
                              <Card
                                key={itemIndex}
                                className={`p-3 hover:shadow-md transition-shadow ${
                                  menu.availability === "in_stock" &&
                                  isMenuAvailable
                                    ? "cursor-pointer"
                                    : "cursor-not-allowed opacity-70"
                                }`}
                                onClick={() => handleItemClick(menu)}
                              >
                                <div className="flex gap-3">
                                  {menu.image && (
                                    <div className="flex-shrink-0 w-16 h-16">
                                      <img
                                        src={`${
                                          Config.env.app.image_url
                                        }/${menu.image.replace("//", "/")}`}
                                        alt={menu.name}
                                        className="h-full w-full rounded-md object-cover"
                                      />
                                    </div>
                                  )}

                                  <div className="flex flex-col justify-between flex-grow">
                                    <div>
                                      <div className="flex items-center justify-between gap-1 mb-1">
                                        <h4 className="font-semibold text-sm line-clamp-1">
                                          {menu?.name?.toString()}
                                        </h4>
                                        {/* {hasSizeRestrictions && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {t("coupon.menu.selected_sizes_only")}
                                        </Badge>
                                      )} */}
                                      </div>

                                      <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                                        {menu?.itemDescription?.toString()}
                                      </p>
                                    </div>

                                    <div className="flex items-baseline gap-2 mt-1">
                                      <p className="text-xs text-gray-500 font-bold">
                                        {selectedType !== "" &&
                                          `${restaurantDetail?.currencyDetails?.symbol} ${originalPrice}`}
                                      </p>
                                      {/* {selectionStep === 2 && (
                                      <p className="text-xs text-green-600 font-bold">
                                        {t("coupon.bogo.free_item")}
                                      </p>
                                    )} */}
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <MenuItem
          isOpen={isItemOpen}
          setOpen={(open, addedToCart, itemData) =>
            handleMenuItemClose(addedToCart, itemData)
          }
          updateData={updateData}
          cartId={cartId}
          id={id}
          authToken={authToken}
          setCartTotal={setCartTotal}
          restaurantDetail={restaurantDetail}
          isDineIn={isDineIn}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          orderingMethod={orderingMethod}
          sizesOrderingMethod={sizesOrderingMethod}
          bogoFlow={true}
          bogoStep={selectionStep}
          couponValue={selectedPromo.coupon}
        />
      </ErrorBoundary>
    </>
  );
}
